import { GenericSuccessResponse } from 'legacy/common/types/response';
import bounceApiClient from 'legacy/common/utils/bounceApiClient';
import {
  GetMyTicketsResultDto,
  MultiTicketDto,
  ReservedTicketDto,
  TicketDto,
  UserTicketDto
} from './types';

export const DEFAULT_PAGE_LIMIT = 18;
export const MAX_TICKETS = 5;

export async function getMyTickets(options: {
  limit?: number;
  pageParam?: number;
}): Promise<GetMyTicketsResultDto> {
  const { data } = await bounceApiClient({
    method: 'get',
    url: '/api/tickets/me',
    params: {
      limit: options?.limit || DEFAULT_PAGE_LIMIT,
      pageNumber: options?.pageParam
    }
  });

  return data;
}

export async function getMyTicketForEvent(eventId: string): Promise<TicketDto> {
  const { data } = await bounceApiClient({
    method: 'get',
    url: `/api/tickets/me/events/${eventId}`
  });

  return data;
}

export async function getMyMultiTicketsForEvent(
  eventId: string
): Promise<MultiTicketDto> {
  const { data } = await bounceApiClient({
    method: 'get',
    url: `/api/tickets/me/events/${eventId}/all`
  });

  return data;
}

export async function getTicketToTransfer(
  ticketId: string
): Promise<UserTicketDto> {
  const { data } = await bounceApiClient({
    method: 'get',
    url: `/api/tickets/${ticketId}/transfer`
  });

  return data;
}

export async function revokeTicket({
  eventId,
  userId
}: {
  eventId: string;
  userId: string;
}): Promise<{ success: boolean }> {
  const { data } = await bounceApiClient({
    method: 'post',
    url: '/api/tickets/revoke',
    data: {
      eventId,
      userId
    }
  });

  return data;
}

export async function rollbackTicket({
  ticketId
}: {
  ticketId: string;
}): Promise<{ success: boolean }> {
  const { data } = await bounceApiClient({
    method: 'post',
    url: 'api/tickets/rollback',
    data: {
      ticketId
    }
  });

  return data;
}

export async function getReservedTicket({
  shareToken
}: {
  shareToken: string;
}): Promise<ReservedTicketDto> {
  const { data } = await bounceApiClient({
    method: 'get',
    url: '/service/tickets/reserved',
    params: {
      shareToken
    }
  });

  return data;
}

export async function claimReservedTicket({
  shareToken,
  isWaiverAccepted
}: {
  shareToken: string;
  isWaiverAccepted: boolean;
}) {
  const { data } = await bounceApiClient({
    method: 'post',
    url: '/api/tickets/claim',
    data: {
      shareToken,
      isWaiverAccepted
    }
  });

  return data;
}

export async function transferTicket({
  ticketId,
  isWaiverAccepted
}: {
  ticketId: string;
  isWaiverAccepted: boolean;
}) {
  const { data } = await bounceApiClient({
    method: 'post',
    url: '/api/tickets/transfer',
    data: {
      ticketId,
      isWaiverAccepted
    }
  });

  return data;
}

export async function refundEventTicket({
  eventId,
  ticketId
}: {
  eventId: string;
  ticketId: string;
}): Promise<GenericSuccessResponse> {
  const { data } = await bounceApiClient({
    method: 'post',
    url: `/api/hosts/events/${eventId}/tickets/${ticketId}/refund`
  });

  return data;
}

export async function refundEventBundle({
  eventId,
  bundleInstanceId
}: {
  eventId: string;
  bundleInstanceId: string;
}): Promise<GenericSuccessResponse> {
  const { data } = await bounceApiClient({
    method: 'post',
    url: `/api/events/${eventId}/tickets/bundles/${bundleInstanceId}/refund`
  });

  return data;
}

export async function requestTicketRefund({
  ticketId
}: {
  ticketId: string;
}): Promise<GenericSuccessResponse> {
  const { data } = await bounceApiClient({
    method: 'post',
    url: `/api/tickets/${ticketId}/refundRequest`
  });

  return data;
}
